<template>
    <div class="Section Pricing" id="pricing">
        <div class="SectionHeader">
            <p class="Subtitle">Spezielle Tarife für Ihre Taxi & Mietwagenflotte.</p>
            <h2 class="Headline">Flexible Lösungen</h2>
        </div>
        <div class="PromotionBanner" v-if="showPromotion">
            <div class="PromotionBadge">
                <i class="ri-discount-percent-line"></i>
                <span>{{ discountBadgeText }}</span>
            </div>

            <div class="CTAContent">
                <div class="CTAHeader">
                    <div class="CTAIcon">
                        <i class="ri-alarm-warning-fill"></i>
                    </div>
                    <h3 class="cta-header-text">
                        Sonderaktion: Nur bis {{ promotionDeadlineFormatted }}!
                    </h3>
                </div>

                <div class="CTABody">
                    <p class="CTAMainText">
                        Sichern Sie sich jetzt
                        <strong>{{ discountPercentage }}% Rabatt auf den Basic Tarif</strong
                        ><span v-if="terminalIncluded">
                            und erhalten Sie das
                            <strong>{{ terminalName }} Terminal kostenlos</strong> dazu</span
                        >! Sparen Sie bei jedem Taxi {{ discountedPrice }}€ pro Monat und
                        profitieren Sie von moderner Zahlungsabwicklung.
                    </p>
                </div>

                <div class="PromotionNote">
                    Angebot gilt nur für Neukunden • Begrenzte Zeit bis {{ promotionDeadlineShort }}
                </div>
                <div class="CTAAction">
                    <router-link to="/register?promotion=true&source=pricing_promotion">
                        <SiteButton>
                            Jetzt {{ discountPercentage }}% sparen & Terminal gratis sichern
                        </SiteButton>
                    </router-link>
                </div>
            </div>
        </div>
        <div class="HeaderInfo">
            <p class="TaxInfo">
                <i class="ri-information-line Icon"></i> Alle Preise verstehen sich zzgl. MwSt.
            </p>
        </div>

        <Pricing :planType="currentPlanType" @onUpgrade="handlePlanUpgrade" />

        <div id="promotion-details" class="PromotionText" v-if="showPromotion">
            <p class="PromotionTextHeader">
                <span>* Wichtige Details zur Aktion</span>
            </p>
            <div class="PromotionTextItems">
                <p>
                    <strong>Nur für Neukunden</strong> - Dieses Angebot gilt ausschließlich für neue
                    TaxiFusion-Kunden
                </p>
                <p>
                    <strong>{{ durationMonths }} Monate Aktionspreis</strong> - Der reduzierte Preis
                    gilt für die ersten {{ durationMonths }} Monate nach Registrierung
                </p>
                <p>
                    <strong>Danach {{ regularPrice }}€/Monat</strong> - Anschließend gilt der
                    Standardpreis, abzüglich eventueller Rabatte
                </p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Card from '@/components/Card.vue';
import SiteButton from '@/components/SiteButton.vue';
import Pricing from '@/components/Pricing.vue';

export default {
    components: {
        Card,
        SiteButton,
        Pricing,
    },
    data() {
        return {
            numberOfTaxis: 0,
            currentPlanType: '',
        };
    },
    computed: {
        ...mapGetters('promotion', [
            'showPromotion',
            'discountPercentage',
            'regularPrice',
            'discountedPrice',
            'durationMonths',
            'terminalIncluded',
            'terminalName',
            'discountBadgeText',
            'promotionDeadlineFormatted',
            'promotionDeadlineShort',
        ]),
        pricePerTaxi() {
            const n = this.numberOfTaxis;
            if (n === 1) {
                return 13;
            } else if (n >= 20) {
                return 13.6;
            } else if (n >= 10) {
                return 14.4;
            } else {
                return this.regularPrice;
            }
        },
        normalMonthlyCost() {
            const cost = this.pricePerTaxi * this.numberOfTaxis;
            return cost.toFixed(2).replace('.', ',');
        },
        promotionMonthlyCost() {
            const cost = this.discountedPrice * this.numberOfTaxis;
            return cost.toFixed(2).replace('.', ',');
        },
        savingsPerMonth() {
            const normalCost = this.pricePerTaxi * this.numberOfTaxis;
            const promotionCost = this.discountedPrice * this.numberOfTaxis;
            const savings = normalCost - promotionCost;
            return savings.toFixed(2).replace('.', ',');
        },
    },
    methods: {
        handlePlanUpgrade(targetPlan) {
            const promotionParam = this.showPromotion ? '&promotion=true' : '';
            this.$router.push(`/register?plan=${targetPlan}${promotionParam}`);
        },
    },
};
</script>

<style lang="scss">
.Pricing {
    @extend %sectionWrapper;
    hyphens: auto;
    position: relative;
    z-index: 1;

    @media (max-width: $breakpoint-tablet-portrait) {
        padding: 50px 20px;
    }

    h2,
    h3,
    h4,
    h5 {
        font-weight: 700;
    }

    .SectionHeader {
        margin: 30px 0 50px;
        text-align: center;

        @media (max-width: $breakpoint-tablet-portrait) {
            text-align: left;
        }
    }

    .Subtitle {
        font-size: 1.25rem;
        color: var(--color-blue-dark);
        margin-bottom: 8px;
    }

    .Headline {
        margin-bottom: 10px;
        font-weight: 700;
        font-size: 2.25rem;
        color: #333;

        @media (max-width: $breakpoint-tablet-portrait) {
            text-align: left;
            font-size: 1.8rem;
        }
    }

    .HeaderInfo {
        text-align: center;
        margin-bottom: 40px;

        @media (max-width: $breakpoint-mobile) {
            text-align: left;
        }
    }

    .SetupFee {
        font-size: 18px;
        margin-bottom: 10px;
        font-weight: 500;
    }

    .TaxInfo {
        font-size: 14px;
        color: #666;
    }

    .Icon {
        margin-right: 6px;
    }

    .PromotionBanner {
        background: linear-gradient(330deg, rgb(137, 19, 62) 0%, rgb(67, 15, 35) 100%);
        border-radius: 20px;
        padding: 40px;
        margin-bottom: 40px;
        color: white;
        box-shadow: 0 20px 40px rgba(197, 34, 83, 0.25);
        border: 0.5px solid rgba(255, 255, 255, 0.1);
        position: relative;

        @media (max-width: 768px) {
            padding: 25px 15px;
            margin-bottom: 30px;
            border-radius: 12px;
        }

        .CTAContent {
            position: relative;
            z-index: 1;
        }

        .PromotionBadge {
            position: absolute;
            top: -20px;
            right: -40px;
            z-index: 2;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
            border: 0.5px solid rgba(255, 255, 255, 0.5);
            border-radius: 25px;
            padding: 8px 16px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            transform: rotate(2deg);

            i {
                color: #d32f2f;
                font-size: 18px;
                font-weight: bold;
            }

            span {
                font-size: 1rem;
                font-weight: 800;
                color: #d32f2f;
                letter-spacing: 0.5px;
                text-transform: uppercase;
            }

            @media (max-width: 768px) {
                top: 15px;
                right: 15px;
                padding: 6px 12px;

                span {
                    font-size: 0.9rem;
                }

                i {
                    font-size: 16px;
                }
            }
        }

        .CTAHeader {
            display: flex;
            align-items: center;
            margin-bottom: 30px;

            @media (max-width: 768px) {
                flex-direction: column;
                text-align: left;
                margin-bottom: 20px;
                align-items: flex-start;
            }

            .CTAIcon {
                display: flex;
                align-items: center;
                margin-right: 20px;

                @media (max-width: 768px) {
                    margin: 0 0 15px 0;
                }

                i {
                    font-size: 2.5rem;
                    color: #fff;

                    @media (max-width: 768px) {
                        font-size: 2rem;
                    }
                }
            }

            h3.cta-header-text {
                font-size: 1.5rem !important;
                font-weight: 700;
                color: white !important;
                margin: 0;
                margin: 0 !important;
                line-height: 1.3;

                @media (max-width: 768px) {
                    font-size: 1.3rem !important;
                    line-height: 1.2 !important;
                }
            }
        }

        .CTABody {
            margin-bottom: 30px;
        }

        .CTAMainText {
            font-size: 1.1rem;
            line-height: 1.7;
            margin: 0 0 30px 0;
            color: rgba(255, 255, 255, 0.95);
            font-weight: 500;

            strong {
                color: white;
                text-decoration: underline;
                text-decoration-color: var(--color-yellow-start);
                text-decoration-thickness: 1px;
                text-underline-offset: 2px;
                font-weight: 700;
            }

            @media (max-width: 768px) {
                font-size: 0.95rem;
                line-height: 1.6;
                margin: 0 0 20px 0;
            }
        }

        .CTASolution {
            h4.cta-solution-text {
                font-size: 1.1rem !important;
                font-weight: 600;
                margin: 0 0 15px 0;
                color: white !important;
            }
        }

        .CTABenefits {
            list-style: none;
            padding: 0;
            margin: 0;
            display: flex;
            flex-direction: column;
            gap: 4px;

            li {
                display: flex;
                align-items: flex-start;
                gap: 12px;
                font-size: 1rem;
                line-height: 1.5;

                i {
                    color: var(--color-yellow-start);
                    font-size: 18px;
                    margin-top: 2px;
                    flex-shrink: 0;
                }

                span {
                    color: rgba(255, 255, 255, 0.95);
                    font-weight: 500;

                    strong {
                        color: white;
                        text-decoration: underline;
                        text-decoration-color: var(--color-yellow-start);
                        text-decoration-thickness: 1px;
                        text-underline-offset: 2px;
                        font-weight: 700;
                    }
                }

                @media (max-width: 768px) {
                    gap: 10px;
                    font-size: 0.95rem;
                    line-height: 1.4;

                    i {
                        font-size: 16px;
                        margin-top: 1px;
                    }
                }
            }
        }

        .CTAAction {
            display: flex;
            margin-top: 30px;

            @media (max-width: 768px) {
                margin-top: 20px;
                justify-content: flex-start;
            }
        }

        .PromotionNote {
            font-size: 0.85rem;
            color: rgba(255, 255, 255, 0.8);
            text-align: center;
            margin-top: 20px;
            line-height: 1.4;

            @media (max-width: 768px) {
                font-size: 0.8rem;
                margin-top: 15px;
            }
        }
    }

    .PromoTag {
        position: absolute;
        top: -15px;
        left: 20px;
        background-color: var(--color-red);
        color: white;
        font-weight: bold;
        padding: 5px 15px;
        border-radius: 20px;
        font-size: 14px;
        z-index: 2;
        box-shadow: 0 4px 6px rgba(239, 68, 68, 0.3);
    }

    .PromotionNote {
        display: flex;
        align-items: center;
        font-size: 0.9rem;
        color: var(--color-text);
        text-align: center;

        i {
            margin-right: 5px;
        }
    }

    .PromotionNote-Conditions {
        font-size: 0.85rem;
        margin-top: 20px;
        margin-bottom: 50px;
        color: var(--color-text);
        text-align: center;
        width: 100%;

        @media (max-width: $breakpoint-mobile) {
            text-align: left;
        }
    }

    .PromotionText {
        margin: 15px auto 100px;
        max-width: 730px;

        @media (max-width: $breakpoint-mobile) {
            margin: 12px 15px 20px;
        }
    }

    .PromotionTextHeader {
        display: inline-flex;
        gap: 6px;
        margin-bottom: 8px;
        font-size: 1.25rem;
        font-weight: 700;
        color: #4a5568;
        justify-content: center;
        align-items: center;

        @media (max-width: $breakpoint-mobile) {
            font-size: 0.85rem;

            i {
                font-size: 0.8rem;
            }
        }
    }

    .PromotionTextItems {
        p {
            margin: 0 0 4px 0;
            font-size: 1rem;
            color: #718096;
            line-height: 1.4;
            text-align: left;
            display: inline-block;
            width: 100%;
            background: white;
            padding: 10px 14px;
            border-radius: 6px;
            border: 0.5px solid rgba(0, 0, 0, 0.1);

            &:last-child {
                margin-bottom: 0;
            }

            strong {
                color: #4a5568;
                font-weight: 600;
            }
        }

        .PromotionFootnote {
            font-size: 0.9rem;
            color: #666;
            font-style: italic;
            margin-top: 8px;
            background: rgba(255, 255, 255, 0.7);
            border: 0.5px solid rgba(0, 0, 0, 0.05);
        }

        @media (max-width: $breakpoint-mobile) {
            p {
                font-size: 0.75rem;
            }
        }
    }

    .PromotionNote-Basic {
        background: #f8f9fa;
        border: 0.5px solid #e9ecef;
        border-radius: 6px;
        padding: 10px 14px;
        margin: 0px 0 20px 0;
        font-size: 0.85rem;
        color: rgb(73, 80, 87);
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;

        .highlight-price {
            color: var(--color-blue-dark);
            font-weight: 600;
        }

        @media (max-width: $breakpoint-mobile) {
            font-size: 0.8rem;
            padding: 8px 12px;
        }
    }

    .PromotionFeature {
        background-color: #f0fff4;
        color: var(--color-green-dark);
        border-radius: 8px;
        padding: 10px !important;
        margin: 10px 0;
        border: 1px dashed var(--color-green-dark);
        font-size: 1rem;

        &::before {
            color: var(--color-green-dark) !important;
        }

        span {
            color: var(--color-green-dark);
            font-weight: 600;
        }
    }

    .Modules {
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 20px;
    }

    .ModulesTitle {
        font-weight: bold;
        font-size: 1.75rem;
        text-align: center;
        margin-bottom: 30px;

        @media (max-width: $breakpoint-mobile) {
            text-align: left;
        }
    }

    .ModulesWrap {
        display: flex;
        gap: 20px;
        justify-content: center;
        @media (max-width: 768px) {
            flex-direction: column;
            gap: 16px;
            padding: 0 2vw;
        }
    }

    .ModuleCard {
        padding: 32px 28px 28px 28px;
        flex: 1;
        max-width: 450px;
        border-radius: 16px;
        box-shadow: 0 2px 16px 0 rgba(30, 34, 90, 0.06);
        border: 0.5px solid #ececf0;
        background-color: #fff;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        position: relative;
        min-height: 260px;
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 6px 24px 0 rgba(30, 34, 90, 0.12);
            transform: translateY(-2px) scale(1.01);
        }

        h5 {
            font-weight: 600;
            font-size: 1.18rem;
            margin: 10px 0 8px 0;
            color: var(--color-blue-dark);
        }

        p {
            font-size: 1rem;
            color: #444;
            line-height: 1.6;
            margin-bottom: 0;
            font-weight: 400;
        }
        @media (max-width: 768px) {
            padding: 22px 10px 20px 10px;
            min-width: 100%;
            max-width: 100%;
        }
    }

    .ModuleBadge {
        display: inline-block;
        font-size: 0.72rem;
        font-weight: 600;
        color: #2a3457;
        background: #eaf1fb;
        border: 1.5px solid #b5c7e6;
        border-radius: 999px;
        padding: 2px 13px 2px 13px;
        margin-bottom: 3px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        box-shadow: none;
        vertical-align: middle;

        i {
            font-size: 1.35rem;
            font-weight: 300;
            margin-right: 4px;
        }
    }

    .ModulePrice {
        display: flex;
        align-items: center;
        font-size: 1.35rem;
        font-weight: 600;
        color: #23234c;
        background: #f7f8fa;
        border: 0.5px solid #e3e4ea;
        border-radius: 8px;
        padding: 10px 18px;
        margin: 18px 0 0 0;
        gap: 7px;
        width: 100%;
        box-shadow: none;
    }
    .ModulePrice i {
        font-size: 1.35rem;
        color: #b0b2c3;
        margin-right: 4px;
    }
    .ModulePriceUnit {
        font-size: 0.95rem;
        font-weight: 400;
        color: #7b7b8b;
        margin-left: 6px;
    }
}
</style>
